import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useAuth } from "./contexts/AuthContext";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import Layout from "./components/Layout/Layout";
import LandingPage from "./pages/LandingPage";
import Features from "./pages/Features";
// import Pricing from "./pages/Pricing"; // Temporarily disabled
import Security from "./pages/Security";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Status from "./pages/Status";
import HelpCenter from "./pages/HelpCenter";
import LoginPage from "./pages/Auth/LoginPage";
import JobSeekerSignup from "./pages/Auth/JobSeekerSignup";
import HRSignup from "./pages/Auth/HRSignup";
import SignupChooser from "./pages/Auth/SignupChooser";
import TermsAndConditions from "./pages/Legal/TermsAndConditions";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import CancellationsRefunds from "./pages/Legal/CancellationsRefunds";
import ShippingPolicy from "./pages/Legal/ShippingPolicy";
import JobSeekerDashboard from "./pages/JobSeeker/Dashboard";
import AdminDashboard from "./pages/Admin/Dashboard";
import ResumeUpload from "./pages/JobSeeker/ResumeUpload";
import Profile from "./pages/JobSeeker/Profile";
import HRProfile from "./pages/HR/Profile";
import AvailableTests from "./pages/JobSeeker/AvailableTests";
import JDUpload from "./pages/HR/JDUpload";
import MyJobs from "./pages/HR/MyJobs";
import JobDetails from "./pages/HR/JobDetails";
import TestGenerator from "./pages/HR/TestGenerator";
import MyTests from "./pages/HR/MyTests";
import TakeTest from "./pages/JobSeeker/TakeTest";
import AIInterview from "./pages/JobSeeker/AIInterview";
import InterviewResults from "./pages/JobSeeker/InterviewResults";
import ScenarioTest from "./pages/JobSeeker/ScenarioTest";
import TestResults from "./pages/JobSeeker/TestResults";
import SkillsValidation from "./pages/JobSeeker/SkillsValidation";
import SkillsTestResults from "./pages/JobSeeker/SkillsTestResults";
import PersonalizedCourse from "./pages/JobSeeker/PersonalizedCourse";
import CourseTest from "./pages/JobSeeker/CourseTest";
import MyCourses from "./pages/JobSeeker/MyCourses";
import Analytics from "./pages/JobSeeker/Analytics";
import Applications from "./pages/JobSeeker/Applications";
import JobSeekerSubscription from "./pages/JobSeeker/Subscription";
import CandidateManagement from "./pages/HR/CandidateManagement";
import TestDetails from "./pages/HR/TestDetails.tsx";
import TestPreview from "./pages/HR/TestPreview";
import HRTestResults from "./pages/HR/TestResults";
import Shortlisted from "./pages/HR/Shortlisted";
import Rejected from "./pages/HR/Rejected";
import HRSubscription from "./pages/HR/Subscription";
import SharedTest from "./pages/SharedTest";
import UserManagement from "./pages/Admin/UserManagement";
import BadgeManagement from "./pages/Admin/BadgeManagement";
import MigrateTests from "./pages/Admin/MigrateTests";
import AdminPanel from "./pages/AdminPanel/AdminPanel";
import HROnboarding from "./pages/HR/HROnboarding";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import HRSubscriptionGuard from "./components/Auth/HRSubscriptionGuard";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "react-hot-toast";

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  // Enable idle timeout only for authenticated users
  // Auto-logout after 15 minutes of inactivity
  useIdleTimeout({
    timeout: 15 * 60 * 1000, // 15 minutes
  });

  // Determine what to show when users access the root path
  const getRootRedirect = () => {
    // If the visitor is not authenticated, show the public landing page
    if (!user) return <LandingPage />;

    // Otherwise, take them straight to the appropriate dashboard
    switch (user.userType) {
      case "jobseeker":
        return <Navigate to="/job-seeker" replace />;
      case "hr":
        return <Navigate to="/hr" replace />;
      case "admin":
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={getRootRedirect()} />

      {/* Public Routes */}
      <Route path="/features" element={<Features />} />
      {/* <Route path="/pricing" element={<Pricing />} /> */} {/* Temporarily disabled */}
      <Route path="/security" element={<Security />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/status" element={<Status />} />
      <Route path="/help-center" element={<HelpCenter />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupChooser />} />
      <Route path="/signup/jobseeker" element={<JobSeekerSignup />} />
      <Route path="/signup/hr" element={<HRSignup />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route
        path="/terms-and-conditions"
        element={<TermsAndConditions />}
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route
        path="/cancellations-refunds"
        element={<CancellationsRefunds />}
      />
      <Route path="/test/share/:testId" element={<SharedTest />} />
      <Route path="/shipping" element={<ShippingPolicy />} />
      
      {/* Separate Admin Panel Route */}
      <Route path="/admin-panel" element={<AdminPanel />} />

      {/* Job Seeker Routes */}
      <Route
        path="/job-seeker"
        element={<Navigate to="/job-seeker/profile" replace />}
      />
      <Route
        path="/job-seeker/tests"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <AvailableTests />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/profile"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/resume-upload"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <ResumeUpload />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/take-test/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <TakeTest />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/ai-interview/:sessionId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <AIInterview />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/interview-results/:sessionId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <InterviewResults />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/scenario-test/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <ScenarioTest />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/results"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <TestResults />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/analytics"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/applications"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <Applications />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/skills-validation"
        element={
          <ProtectedRoute userType="jobseeker">
            <SkillsValidation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/skills-test-results/:testId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <SkillsTestResults />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/my-courses"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <MyCourses />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/personalized-course/:courseId"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <PersonalizedCourse />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/course-test/:courseId"
        element={
          <ProtectedRoute userType="jobseeker">
            <CourseTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-seeker/subscription"
        element={
          <ProtectedRoute userType="jobseeker">
            <Layout userType="jobseeker">
              <JobSeekerSubscription />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* HR Routes */}
      {/* HR Onboarding - No subscription check */}
      <Route
        path="/hr/onboarding"
        element={
          <ProtectedRoute userType="hr">
            <HROnboarding />
          </ProtectedRoute>
        }
      />

      {/* HR Dashboard and other routes - Require subscription */}
      <Route
        path="/hr"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRProfile />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/profile"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRProfile />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/jd-upload"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <JDUpload />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-jobs"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <MyJobs />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-jobs/:jobId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <JobDetails />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-generator"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestGenerator />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-tests"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <MyTests />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/my-tests/:testId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestDetails />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-preview"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <TestPreview />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/candidates"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <CandidateManagement />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/test-results/:testId"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <HRTestResults />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/shortlisted"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <Shortlisted />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/rejected"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscriptionGuard>
              <Layout userType="hr">
                <Rejected />
              </Layout>
            </HRSubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/subscription"
        element={
          <ProtectedRoute userType="hr">
            <HRSubscription />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/badges"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <BadgeManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/migrate-tests"
        element={
          <ProtectedRoute userType="admin">
            <Layout userType="admin">
              <MigrateTests />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-neutral-100 text-neutral-800">
            <ScrollToTop />
            <AppRoutes />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  borderRadius: "12px",
                  background: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  color: "#1F2937",
                },
                success: {
                  iconTheme: {
                    primary: "#059669",
                    secondary: "#ECFDF5",
                  },
                },
                error: {
                  iconTheme: {
                    primary: "#E11D48",
                    secondary: "#FFF1F2",
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
